import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { IAlbumImage } from "../../../hooks/albumImages";
import { Album3DRepresentation, Album3DRepresentationHandle } from "./index";
import { ImageCache } from "../../../models/ImageCache";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { useGetImage } from "../../../hooks/images";

export const Album3DRepresentationContainer = forwardRef<
  Album3DRepresentationContainerHandle,
  Album3DRepresentationContainerProps
>(
  (
    {
      frontCover,
      defaultFrontImage,
      backCover,
      spine,
      size,
      initialRotation,
      isLoadingFrontCover,
      setIsLoadingFrontCover,
      setIsDragging
    },
    ref
  ) => {
    const { getImage } = useGetImage();
    const [frontImage, setFrontImage] = useState<string>(defaultFrontImage);
    const [backImage, setBackImage] = useState<string>();
    const [spineImage, setSpineImage] = useState<string>();
    const fetchFrontImageCount = useRef(0);
    const fetchBackImageCount = useRef(0);
    const fetchSpineImageCount = useRef(0);
    const genericBackImage = new URL("../../../images/generic_back.jpg", import.meta.url).href;
    const genericSleeveImage = new URL("../../../images/generic_sleeve.jpg", import.meta.url).href;
    const genericSpineImage = new URL("../../../images/generic_spine.png", import.meta.url).href;
    const setImage = (image: string, type: ImageType) => {
      switch (type) {
        case "front":
          setFrontImage(image);
          break;
        case "back":
          setBackImage(image);
          break;
        case "spine":
          setSpineImage(image);
          break;
      }
    };
    const incrementFetchImageCount = (type: ImageType) => {
      switch (type) {
        case "front":
          fetchFrontImageCount.current += 1;
          break;
        case "back":
          fetchBackImageCount.current += 1;
          break;
        case "spine":
          fetchSpineImageCount.current += 1;
          break;
      }
    };
    const getFetchImageCount = (type: ImageType) => {
      switch (type) {
        case "front":
          return fetchFrontImageCount.current;
        case "back":
          return fetchBackImageCount.current;
        case "spine":
          return fetchSpineImageCount.current;
      }
    };
    const fetchImage = async (albumImage: IAlbumImage, type: ImageType) => {
      incrementFetchImageCount(type);
      if (!albumImage || !albumImage.hasImage) {
        setImage(undefined, type);
        if (type === "front") setIsLoadingFrontCover(false);
        return;
      }
      if (type === "front") setIsLoadingFrontCover(true);
      const fetchCount = getFetchImageCount(type);
      const response = await getImage({
        name: albumImage.uuid,
        width: ImageCache.bigAlbumImageSize,
        height: ImageCache.bigAlbumImageSize
      });
      // Discard if albumImage changed
      if (getFetchImageCount(type) !== fetchCount) return;
      if (StatusCodesHelper.isSuccessful(response.status)) {
        setImage(response.body, type);
      }
      if (type === "front") setIsLoadingFrontCover(false);
    };
    useEffect(() => {
      fetchImage(frontCover, "front").then();
    }, [frontCover]);
    useEffect(() => {
      fetchImage(backCover, "back").then();
    }, [backCover]);
    useEffect(() => {
      fetchImage(spine, "spine").then();
    }, [spine]);
    useEffect(() => {
      setFrontImage(defaultFrontImage);
    }, [defaultFrontImage]);

    const album3DRepresentationRef = useRef<Album3DRepresentationHandle>(null);
    useImperativeHandle(ref, () => ({
      reset: () => {
        album3DRepresentationRef.current?.reset();
      },
      refreshImages: () => {
        fetchImage(frontCover, "front").then();
        fetchImage(backCover, "back").then();
        fetchImage(spine, "spine").then();
      }
    }));

    return (
      <div className={styles.container}>
        <Album3DRepresentation
          ref={album3DRepresentationRef}
          frontCover={frontImage}
          backCover={backImage}
          spine={spineImage}
          frontThumbnail={frontCover?.thumbnail}
          backThumbnail={backCover?.thumbnail}
          spineThumbnail={spine?.thumbnail}
          defaultCover={genericSleeveImage}
          defaultBack={genericBackImage}
          defaultSpine={genericSpineImage}
          isLoadingFrontCover={isLoadingFrontCover}
          size={size}
          initialRotation={initialRotation}
          setIsDraggingExternal={setIsDragging}
        />
      </div>
    );
  }
);

type ImageType = "front" | "back" | "spine";

export interface Album3DRepresentationContainerHandle {
  reset: () => void;
  refreshImages: () => void;
}

interface Album3DRepresentationContainerProps {
  frontCover: IAlbumImage;
  defaultFrontImage?: string;
  backCover: IAlbumImage;
  spine: IAlbumImage;
  size: number;
  initialRotation?: number;
  isLoadingFrontCover: boolean;
  setIsLoadingFrontCover: (isLoading: boolean) => void;
  setIsDragging?: (isDragging: boolean) => void;
}
